import React, { useEffect, useMemo, useState } from "react";
import { LayoutGrid, Search } from "lucide-react";
import { api, API } from "@/lib/api";
import SpaceCard from "@/components/SpaceCard";
import BookingDialog from "@/components/BookingDialog";
import BannerCarousel from "@/components/BannerCarousel";
import MyReservationsDialog from "@/components/MyReservationsDialog";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import FloatingMenuButton from "@/components/FloatingMenuButton";
import usePageTexts from "@/hooks/usePageTexts";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1785149882009-40d00251045c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const LOGO_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_arena-dubal/artifacts/dgj3odz1_Logo%20Dubal%20branca.png";

const FILTERS = [
  { id: "all", label: "Todas" },
  { id: "coberta", label: "Cobertas" },
  { id: "descoberta", label: "Descoberta" },
  { id: "churrasqueira", label: "Churrasqueira" },
];

export default function HomePage() {
  const t = usePageTexts();
  const [spaces, setSpaces] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [heroImg, setHeroImg] = useState(null);
  const [heroVideo, setHeroVideo] = useState(null);
  const [logoImg, setLogoImg] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [myOpen, setMyOpen] = useState(false);

  useEffect(() => {
    api.get("/spaces").then((res) => setSpaces(res.data)).catch(() => {});
    api
      .get("/settings")
      .then((res) => {
        setHeroImg(
          res.data.hero_image
            ? `${API}/settings/hero-image?v=${encodeURIComponent(res.data.hero_image_updated_at)}`
            : DEFAULT_HERO
        );
        setHeroVideo(
          res.data.hero_video
            ? `${API}/settings/hero-video?v=${encodeURIComponent(res.data.hero_video_updated_at)}`
            : null
        );
        setLogoImg(
          res.data.logo_image
            ? `${API}/settings/logo-image?v=${encodeURIComponent(res.data.logo_image_updated_at)}`
            : LOGO_URL
        );
      })
      .catch(() => {
        setHeroImg(DEFAULT_HERO);
        setLogoImg(LOGO_URL);
      });
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal:not(.reveal-visible)").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [spaces]);

  const filtered = useMemo(
    () => (filter === "all" ? spaces : spaces.filter((s) => s.type === filter)),
    [spaces, filter]
  );

  const openBooking = (space) => {
    setSelected(space);
    setOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FBFBFB]">
      {/* HERO */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video
              src={heroVideo}
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-video"
              className="h-full w-full object-cover"
            />
          ) : (
            heroImg && (
              <img
                src={heroImg}
                alt=""
                onLoad={() => setHeroLoaded(true)}
                className={`h-full w-full object-cover transition-opacity duration-700 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
              />
            )
          )}
          <div className="absolute inset-0 bg-[#0b1610]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1610] via-[#0b1610]/40 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-10 text-center sm:pb-20 sm:pt-14">
          {logoImg && (
            <img
              src={logoImg}
              alt="Dubal Esportes"
              data-testid="hero-logo"
              onLoad={() => setLogoLoaded(true)}
              className={`animate-float mx-auto w-64 max-w-full transition-opacity duration-700 sm:w-80 lg:w-96 ${logoLoaded ? "opacity-100" : "opacity-0"}`}
            />
          )}
          <p className="animate-fade-up mx-auto mt-2 max-w-md text-base font-medium text-emerald-100/90 sm:text-lg" style={{ animationDelay: "0.1s" }}>
            {t("reservas_tagline")}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              data-testid="open-my-reservations"
              onClick={() => setMyOpen(true)}
              style={{ animationDelay: "0.25s" }}
              className="animate-fade-up inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20 hover:scale-105"
            >
              <Search className="h-4 w-4" /> Consultar minhas reservas
            </button>
          </div>
        </div>
      </header>

      {/* PUBLICIDADE */}
      <BannerCarousel />

      {/* SPACES */}
      <main className="mx-auto max-w-6xl px-5 pt-10">
        <div className="reveal relative z-10 flex items-center gap-2">
          <LayoutGrid className="h-6 w-6 text-[#1B7A45]" />
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
            {t("reservas_section_title")}
          </h2>
        </div>

        <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`filter-pill-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                  active
                    ? "bg-[#1B7A45] text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:border-[#1B7A45] hover:text-[#1B7A45]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((space, i) => (
            <SpaceCard key={space.id} space={space} index={i} onReserve={openBooking} />
          ))}
        </div>
      </main>

      <ReviewsSection />

      <Footer />

      <BookingDialog space={selected} open={open} onOpenChange={setOpen} />
      <MyReservationsDialog open={myOpen} onOpenChange={setMyOpen} />
      <FloatingMenuButton />
    </div>
  );
}
